import { READ_TIMEOUT_MS, runInEditQueue } from "@Utils/doc/editQueue";
import { redlineParagraph } from "@Utils/doc/wordDocument";
import { errorOf, EXEC_TIMEOUT_MS, execInputProblem, FRAME_PORT, FRAME_READY, FRAME_STOP, runExecSession, sandboxSrcdoc, supportedRequirements, wordEnums } from "@Utils/doc/wordExec";
import sandboxRuntime from "@Utils/doc/wordExecSandbox.js?raw";
import { create } from "zustand";
import { useChatComposerStore } from "../../store/chatComposerStore";
import { useChatSessionStore } from "../../store/chatSessionStore";
import { uploadSnapshot } from "./wordSnapshots";
const SRCDOC = sandboxSrcdoc(sandboxRuntime);
function openSandbox() {
    return new Promise((resolve, reject)=>{
        const iframe = document.createElement("iframe");
        iframe.setAttribute("sandbox", "allow-scripts");
        iframe.setAttribute("aria-hidden", "true");
        iframe.tabIndex = -1;
        iframe.style.display = "none";
        iframe.srcdoc = SRCDOC;
        const channel = new MessageChannel();
        const escaped = new AbortController();
        let loads = 0;
        let isReady = false;
        const stopTimer = [];
        const destroy = ()=>{
            stopTimer.forEach((stop)=>stop());
            iframe.contentWindow?.postMessage({
                type: FRAME_STOP
            }, "*");
            channel.port1.close();
            iframe.remove();
        };
        const handOff = ()=>{
            if (!isReady || loads !== 1 || !iframe.contentWindow) return;
            stopTimer.forEach((stop)=>stop());
            iframe.contentWindow.postMessage({
                type: FRAME_PORT
            }, "*", [
                channel.port2
            ]);
            resolve({
                port: channel.port1,
                signal: escaped.signal,
                destroy
            });
        };
        const onReady = (event)=>{
            const data = event.data;
            if (event.source !== iframe.contentWindow || data?.type !== FRAME_READY) return;
            window.removeEventListener("message", onReady);
            isReady = true;
            handOff();
        };
        iframe.addEventListener("load", ()=>{
            loads += 1;
            if (loads > 1) escaped.abort();
            else handOff();
        });
        const timer = setTimeout(()=>{
            window.removeEventListener("message", onReady);
            destroy();
            reject(new Error("The code sandbox did not start."));
        }, EXEC_TIMEOUT_MS);
        stopTimer.push(()=>clearTimeout(timer));
        window.addEventListener("message", onReady);
        document.body.appendChild(iframe);
    });
}
const objectModel = ()=>({
        ClientObject: OfficeExtension.ClientObject,
        ClientResult: OfficeExtension.ClientResult,
        TrackedObjects: OfficeExtension.TrackedObjects,
        Paragraph: Word.Paragraph
    });
async function runInWord({ code, mode }) {
    const frame = await openSandbox();
    try {
        return await Word.run(async (context)=>{
            const { document: document1 } = context;
            let tracking;
            if (mode === "write") {
                document1.load("changeTrackingMode");
                await context.sync();
                tracking = document1.changeTrackingMode;
                document1.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
                await context.sync();
            }
            try {
                return await runExecSession(frame.port, {
                    code,
                    mode,
                    context,
                    model: objectModel(),
                    enums: wordEnums(Word),
                    requirements: supportedRequirements((name, version)=>Office.context.requirements.isSetSupported(name, version)),
                    signal: frame.signal,
                    redline: (paragraph, text)=>redlineParagraph(context, paragraph, text)
                });
            } finally{
                if (tracking !== undefined) {
                    document1.changeTrackingMode = tracking;
                    await context.sync();
                }
            }
        });
    } finally{
        frame.destroy();
    }
}
export const useWordExecCards = create(()=>({
        cards: {}
    }));
const decisions = new Map();
const setCard = (toolCallId, card)=>useWordExecCards.setState((state)=>({
            cards: {
                ...state.cards,
                [toolCallId]: card
            }
        }));
function askApproval(toolCallId) {
    setCard(toolCallId, {
        status: "waiting"
    });
    return new Promise((resolve)=>decisions.set(toolCallId, resolve));
}
export function answerWordExec(toolCallId, approved) {
    const decide = decisions.get(toolCallId);
    decisions.delete(toolCallId);
    decide?.(approved);
}
export async function executeWordExec(input, toolCallId) {
    const problem = execInputProblem(input) ?? (toolCallId ? null : "word.exec needs a tool call id.");
    if (problem) return {
        ok: false,
        error: {
            name: "TypeError",
            message: problem
        },
        logs: []
    };
    const exec = input;
    const id = toolCallId;
    const isWrite = exec.mode === "write";
    const isAuto = useChatComposerStore.getState().wordApplyMode === "auto";
    if (isWrite && !isAuto && !await askApproval(id)) {
        const declined = {
            ok: false,
            declined: true
        };
        setCard(id, {
            status: "done",
            output: declined
        });
        return declined;
    }
    const { setApplying } = useChatSessionStore.getState();
    if (isWrite) {
        if (!isAuto) setCard(id, {
            status: "running"
        });
        setApplying(id, true);
    }
    try {
        const output = await runInEditQueue(()=>runInWord(exec), isWrite ? Infinity : READ_TIMEOUT_MS).catch((error)=>({
                ok: false,
                error: errorOf(error),
                logs: []
            }));
        if (isWrite) {
            await uploadSnapshot().catch((error)=>console.warn("[word.exec] snapshot upload failed:", error));
            setCard(id, {
                status: "done",
                output
            });
        }
        return output;
    } finally{
        if (isWrite) setApplying(id, false);
    }
}
